'use client'

import GridIcon from '@/components/icons/grid'
import HelmetIcon from '@/components/icons/helmet-icon'
import NavigationIcon from '@/components/icons/navigation'
import { cn } from '@/lib/utils'
import { Flag, List, ScrollText, SettingsIcon } from 'lucide-react'
import Link from 'next/link'
import { useMemo } from 'react'
import MobileDropdownNav from './mobile-dropdown-nav'

const itemsSection = [
  {
    title: 'Overview',
    urlSnippet: 'overview',
    icon: <GridIcon className="stroke-neutral-blue-300 stroke-[1.5px]" />,
  },
  {
    title: 'Applications',
    urlSnippet: 'applications',
    icon: <List className="stroke-neutral-blue-300" />,
  },
  {
    title: 'Manage Drivers',
    urlSnippet: 'drivers',
    icon: <HelmetIcon className="stroke-neutral-blue-300" />,
  },
  {
    title: 'Events',
    urlSnippet: 'events',
    icon: <NavigationIcon className="rotate-45 stroke-neutral-blue-300" />,
    showItem: (championship) => !championship?.isSingleEvent,
  },
  {
    title: 'Results & Standings',
    urlSnippet: 'results',
    icon: <Flag className="stroke-neutral-blue-300" />,
  },
  {
    title: 'Access',
    urlSnippet: 'whitelist',
    icon: <ScrollText className="stroke-neutral-blue-300" />,
    showItem: (championship) => championship?.isWhitelistOnly,
  },
]

export default function ManageContent({
  params,
  pathname,
  router,
  community,
  hasOutstandingApplications,
}) {
  const championship = useMemo(() => {
    if (!community) return null
    return community.championships.find((c) => c.id === params.championshipId)
  })

  return (
    <>
      <div className="tablet:hidden">
        <MobileDropdownNav
          options={itemsSection}
          extraOptions={[
            {
              title: `Edit ${championship?.isSingleEvent ? 'Event' : 'Championship'}`,
              urlSnippet: 'edit/configuration',
              icon: <SettingsIcon />,
            },
          ]}
          baseUrl={`/manage/${params.communityId}/racing/${params.championshipId}`}
          hasOutstandingApplications={hasOutstandingApplications}
        />
      </div>

      <div className="flex flex-col max-tablet:hidden">
        <p className="mb-3 text-p-1 font-bold text-neutral-blue-400">Manage</p>

        {itemsSection
          .filter((i) => (i.showItem ? i.showItem(championship) : true))
          .map((item) => (
            <div
              key={item.title}
              onClick={() => {
                router.replace(
                  `/manage/${params.communityId}/racing/${params.championshipId}/${item.urlSnippet}`,
                )
                router.refresh()
              }}
              className="relative flex cursor-pointer items-center gap-2 rounded-sm p-2 transition-all hover:bg-neutral-blue-600"
            >
              <div
                className={cn(
                  'flex aspect-square items-center justify-center rounded-sm bg-neutral-blue-700 stroke-neutral-blue-200 p-1 [&>svg]:size-5',
                  {
                    'bg-shades-0 [&>svg]:stroke-neutral-blue-900':
                      pathname.includes(item.urlSnippet),
                  },
                )}
              >
                {item.icon}
              </div>
              <p className="text-p-1 font-bold">{item.title}</p>
              {item.urlSnippet === 'applications' &&
                hasOutstandingApplications && (
                  <div className="absolute right-6 top-2 h-3 w-3 rounded-full bg-orange-500"></div>
                )}
            </div>
          ))}
        <div className="divide my-4 bg-neutral-blue-700"></div>

        <Link
          href={`/manage/${params.communityId}/racing/${params.championshipId}/edit/configuration`}
          className="mt-1 flex items-center gap-2 rounded-sm p-2 transition-all hover:bg-neutral-blue-600"
        >
          <div className="flex aspect-square items-center justify-center rounded-sm bg-neutral-blue-700 stroke-neutral-blue-200 p-1">
            <SettingsIcon className="size-5" />
          </div>
          <p className="text-p-1 font-bold">
            Edit {championship?.isSingleEvent ? 'Event' : 'Championship'}
          </p>
        </Link>
      </div>
    </>
  )
}
